import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { VisibilityOutlinedIcon } from "../../utilities/icons";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import Cookies from "js-cookie";
import { activityLogsApi } from "../../apiCalls/Apicalls";
import ActivityDetailsDrawer from "../home/ActivityDetailsDrawer";
import { theme } from "../../utilities/dataGridHelpers";
import {
  buildActivitySummary,
  formatActivityTimestamp,
} from "../../utilities/activityHelpers";

const filterOptions = [
  { key: "all", label: "All Activity" },
  { key: "user", label: "User Events" },
  { key: "project", label: "Project Events" },
  { key: "role", label: "Role Events" },
];

function AuditLogs() {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterMode, setFilterMode] = useState("all");
  const [selectedActivity, setSelectedActivity] = useState(null);

  useEffect(() => {
    const authConfig = {
      headers: {
        Authorization: Cookies.get("jwtToken"),
      },
    };

    setLoading(true);
    axios
      .get(activityLogsApi, authConfig)
      .then((response) => {
        setAuditLogs(response.data || []);
      })
      .catch((error) => {
        console.error("Error fetching audit logs:", error);
        setAuditLogs([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    return auditLogs.map((item) => {
      const summary = buildActivitySummary(item);
      return {
        id: summary.id,
        action: summary.actionLabel,
        entityType: item?.entityType || "Unknown",
        actor: summary.actorName,
        target: summary.targetName,
        status: item?.status || "success",
        time: summary.time,
        rawTimestamp: item?.timestamp,
        raw: item,
        color: summary.color,
      };
    });
  }, [auditLogs]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const entityType = row.entityType.toLowerCase();
      const matchesFilter =
        filterMode === "all" ||
        (filterMode === "user" && entityType.includes("user")) ||
        (filterMode === "project" && entityType.includes("project")) ||
        (filterMode === "role" && entityType.includes("role"));

      const haystack = [
        row.action,
        row.entityType,
        row.actor,
        row.target,
        row.status,
        formatActivityTimestamp(row.rawTimestamp),
        JSON.stringify(row.raw?.metadata || {}),
      ]
        .join(" ")
        .toLowerCase();

      return matchesFilter && haystack.includes(search.toLowerCase());
    });
  }, [filterMode, rows, search]);

  const columns = [
    {
      field: "action",
      headerName: "Action",
      flex: 1.1,
      minWidth: 180,
      sortable: false,
    },
    {
      field: "actor",
      headerName: "Actor",
      flex: 0.8,
      minWidth: 140,
      sortable: false,
    },
    {
      field: "target",
      headerName: "Target",
      flex: 1,
      minWidth: 180,
      sortable: false,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.7,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          className={`ax-activity-chip ${params.row.color}`}
          size="small"
        />
      ),
    },
    {
      field: "time",
      headerName: "Timestamp",
      flex: 1,
      minWidth: 200,
      sortable: false,
    },
    {
      field: "details",
      headerName: "Details",
      flex: 0.7,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="text"
          startIcon={<VisibilityOutlinedIcon fontSize="small" />}
          onClick={() => setSelectedActivity(params.row.raw)}
          sx={{ fontSize: "0.78rem" }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="ax-page-grid">
        <div className="ax-card">
          <div className="ax-section-head">
            <div>
              <h2>Audit Activity Timeline</h2>
              <p>
                Review every identity, project, and permission change with full
                request context.
              </p>
            </div>
            <div className="ax-audit-summary">
              {loading ? <CircularProgress size={20} /> : filteredRows.length}
              <span>records shown</span>
            </div>
          </div>

          <div
            className="ax-grid-toolbar"
            style={{
              padding: "0",
              borderBottom: 0,
              marginBottom: 20,
            }}
          >
            <div className="ax-filters">
              {filterOptions.map((item) => (
                <button
                  key={item.key}
                  className={`ax-filter-chip${filterMode === item.key ? " active" : ""}`}
                  onClick={() => setFilterMode(item.key)}
                  type="button"
                >
                  {item.label}
                </button>
              ))}
            </div>

            <TextField
              placeholder="Search audit logs..."
              size="small"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: { xs: "100%", sm: 280 } }}
            />
          </div>

          <div className="ax-audit-highlights">
            {rows.slice(0, 3).map((row) => (
              <button
                key={`highlight-${row.id}`}
                className={`ax-audit-highlight ${row.color}`}
                onClick={() => setSelectedActivity(row.raw)}
                type="button"
              >
                <strong>{row.action}</strong>
                <span>{row.target}</span>
                <small>{row.time}</small>
              </button>
            ))}
            {!loading && rows.length === 0 ? (
              <Box py={4} sx={{ gridColumn: "1 / -1", textAlign: "center" }}>
                <Typography
                  sx={{ color: "var(--ax-ink-3)", fontSize: "0.86rem" }}
                >
                  No audit activity available yet.
                </Typography>
              </Box>
            ) : null}
          </div>

          <div
            className="ax-card ax-table-shell"
            style={{ padding: 0, marginTop: 20 }}
          >
            <ThemeProvider theme={theme}>
              <DataGrid
                sx={{
                  height: "calc(100vh - 380px)",
                  minHeight: 460,
                }}
                rows={filteredRows}
                columns={columns}
                loading={loading}
                disableColumnMenu
                disableRowSelectionOnClick
                rowHeight={62}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10 },
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
              />
            </ThemeProvider>
          </div>
        </div>
      </div>

      <ActivityDetailsDrawer
        open={Boolean(selectedActivity)}
        activity={selectedActivity}
        onClose={() => setSelectedActivity(null)}
      />
    </>
  );
}

export default AuditLogs;
